import styled from 'styled-components'
import { Tooltip } from 'react-tooltip'

import Picture from '../Picture'
import Icon from '../Icon'
import { mq, pxToRem, sizes } from '../../theme'

export const Container = styled.div<{ $color?: string }>`
  --video-color: ${({ $color }) => $color};
  position: relative;
  overflow: clip;
  display: flex;
  flex-flow: column;
  aspect-ratio: 16 / 9;
  width: 100%;
  height: auto;
  & iframe {
    display: block;
  }
  // vidstack stuff
  & .vs-player {
    flex: 1;
    display: flex;
    flex-flow: column;
    background: #000;
  }
  & .vs-player button {
    display: flex;
    align-items: center;
    justify-content: center;
    background: unset;
  }
  & .vs-player button svg {
    pointer-events: none;
  }
  & .vs-provider {
    flex: 1;
  }
  & .vs-provider iframe {
    width: 100%;
    height: 100%;
  }
  & .vs-controls {
    position: absolute;
    inset: 0;
  }
  & .vs-player[data-fullscreen] .vs-controls {
    position: relative;
    inset: unset;
  }
  & .vs-controls__bottom {
    position: absolute;
    inset: 0;
    inset-block-start: auto;
    z-index: 5;
    display: flex;
    flex-flow: row;
    align-items: center;
    gap: ${pxToRem(16)};
    padding: ${pxToRem(16)};
    border: 1px solid rgba(0, 0, 0, 0.5);
    background: ${({ theme }) => theme.colors.white};
    opacity: 0;
    translate: 0 100%;
    transition:
      translate 0.3s ease-out,
      opacity 0.3s ease-out;
  }
  &:hover .vs-controls__bottom,
  &:focus-within .vs-controls__bottom {
    translate: 0 0;
    opacity: 1;
  }
  & .vs-player[data-fullscreen] .vs-controls__bottom {
    position: relative;
    opacity: 1;
    translate: 0 0;
  }
  & .vs-progress {
    position: relative;
    flex: 1 1 auto;
    height: 12px;
    margin-inline: ${pxToRem(8)};
    border: 1px solid rgba(0, 0, 0, 0.5);
    background: ${({ theme }) => theme.colors.white};
    &.is-alone {
      margin-inline: 0;
    }
  }
  & .vs-progress::before,
  & .vs-progress__track,
  & .vs-progress__fill,
  & .vs-progress__progress {
    position: absolute;
    inset: 0;
  }
  & .vs-progress__track {
    overflow: hidden;
  }
  & .vs-progress__fill,
  & .vs-progress__progress {
    inset-block-end: 0;
    will-change: width;
    translate: 0 0 0;
  }
  & .vs-progress__fill {
    z-index: 5;
    width: var(--slider-fill);
    background: var(--video-color);
    border-inline-end: 1px solid rgba(0, 0, 0, 0.5);
  }
  & .vs-progress[aria-valuenow='0'] .vs-progress__fill {
    opacity: 0;
  }
  & .vs-progress[aria-valuenow='100'] .vs-progress__fill {
    border-color: var(--video-color);
  }
  & .vs-progress__progress {
    z-index: 4;
    width: var(--slider-progress);
    background: var(--video-color);
    opacity: 0.2;
  }
  & .vs-progress__thumb {
    background: pink;
    height: 10px;
  }
  & .vs-volume {
    position: relative;
    display: flex;
    width: ${pxToRem(40)};
    height: 9px;
    align-items: center;
    cursor: pointer;
    &::before {
      content: '';
      position: absolute;
      z-index: 1;
      inset-inline: 0;
      inset-block-start: 50%;
      height: 1px;
      translate: 0 -50%;
      background: rgba(0, 0, 0, 0.3);
    }
  }
  & .vs-volume__track {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    width: var(--slider-fill);
    height: 3px;
    background: ${({ theme }) => theme.colors.black};
    cursor: pointer;
    &:focus:not(:focus-visible) {
      outline: 0;
    }
  }
  & .vs-volume__thumb {
    position: absolute;
    z-index: 3;
    inset-block-start: 50%;
    inset-inline-start: var(--slider-fill);
    height: 9px;
    width: 3px;
    background: ${({ theme }) => theme.colors.black};
    translate: 0 -50%;
  }
  & .vs-controls__progress {
    position: absolute;
    inset: 0;
    inset-block-start: auto;
    opacity: 1;
    pointer-events: none;
    transition: opacity 0.15s ease-out 0.15s;
  }
  &:hover .vs-controls__progress,
  &:focus-within .vs-controls__progress {
    opacity: 0;
    transition: opacity 0.15s ease-out 0s;
  }
  & .vs-player[data-fullscreen] .vs-controls__progress {
    opacity: 0;
  }
`
export const Cover = styled.div`
  position: absolute;
  inset: -1px;
  z-index: 10;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  background: ${({ theme }) => theme.colors.black};
  &[hidden] {
    display: flex;
    opacity: 0;
    visibility: hidden;
  }
`
export const Play = styled.span<{ $color: string }>`
  position: sticky;
  z-index: 2;
  inset-inline-start: 0;
  inset-block-start: 0;
  display: flex;
  align-items: center;
  gap: ${pxToRem(24)};
  padding: ${pxToRem(24)};
  background: ${({ $color }) => $color};
  @media ${mq(sizes.desktop)} {
    padding: ${pxToRem(30)};
  }
`
export const _Icon = styled(Icon)`
  flex: 0 0 auto;
  aspect-ratio: 1;
  width: ${pxToRem(16)};
  @media ${mq(sizes.desktop)} {
    width: ${pxToRem(24)};
  }
`
export const Label = styled.span`
  ${({ theme }) => theme.textStyles.m375_h4}
  display: none;
  ${Container}:hover &,
  ${Container}:focus & {
    display: block;
  }
`
export const _Picture = styled(Picture)`
  position: absolute;
  inset: 0;
  z-index: 1;
  object-fit: cover;
  transition: transform 0.3s ease-out;
  ${Container}:hover &,
  ${Play}:focus & {
    transform: scale(1.05);
  }

  // Fix visual artifact when cover and player sizes are different
  & img {
    height: 100%;
    object-fit: cover;
  }
`
// Vidstack stuff ("__" to avoid conflict with Vidstack components names )
export const Skip = styled.button``
export const Transcript = styled.button``
export const __Time = styled.div`
  ${({ theme }) => theme.textStyles.m375_bodySmall}
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: ${pxToRem(4)};
  @media ${mq(sizes.desktop)} {
    ${({ theme }) => theme.textStyles.d1440_body}
  }
`
export const __Volume = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${pxToRem(8)};
  & .vs-volume {
    display: none;
  }
  &:hover .vs-volume,
  &:focus-within .vs-volume {
    display: flex;
  }
`
export const _Tooltip = styled(Tooltip)`
  &.react-tooltip {
    ${({ theme }) => theme.textStyles.m375_bodySmall}
    padding: ${pxToRem(4)} ${pxToRem(8)};
    border: 1px solid rgba(0, 0, 0, 0.5);
    border-radius: 0;
    font-size: ${pxToRem(11)};
    color: ${({ theme }) => theme.colors.black};
    background: ${({ theme }) => theme.colors.white};
    opacity: 1;
    z-index: ${({ theme }) => theme.zIndexes.modal};
  }
`
